import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import axios from "axios";
import API_ENDPOINTS from "config/api";

export default function PuntoVenta() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [descuento, setDescuento] = useState(0);
  const [metodoPago, setMetodoPago] = useState("");
  const [referencia, setReferencia] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [modalEliminar, setModalEliminar] = useState({
    open: false,
    index: null,
    producto: null,
  });

  useEffect(() => {
    fetchProductos();
    fetchClientes();
    fetchMetodosPago();
  }, []);

  const fetchProductos = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.PRODUCTOS}?estatus=activo`);
      setProductos(response.data);
    } catch (error) {
      console.error("Error al cargar productos:", error);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.CLIENTES}?estatus=activo`);
      setClientes(response.data);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
    }
  };

  const fetchMetodosPago = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.METODOS_PAGO}?activo=1`);
      setMetodosPago(response.data);
    } catch (error) {
      console.error("Error al cargar métodos de pago:", error);
      // Métodos de pago temporales como fallback
      setMetodosPago([
        { id: 1, nombre: "Efectivo", requiere_referencia: false },
        { id: 2, nombre: "Tarjeta de Débito", requiere_referencia: true },
        { id: 3, nombre: "Tarjeta de Crédito", requiere_referencia: true },
        { id: 4, nombre: "Transferencia", requiere_referencia: true },
        { id: 5, nombre: "Pago Móvil", requiere_referencia: true },
      ]);
    }
  };

  const agregarAlCarrito = () => {
    if (!productoSeleccionado) {
      alert("Seleccione un producto");
      return;
    }

    if (cantidad <= 0) {
      alert("La cantidad debe ser mayor a 0");
      return;
    }

    const productoExistente = carrito.find((item) => item.producto.id === productoSeleccionado.id);

    if (productoExistente) {
      setCarrito(
        carrito.map((item) =>
          item.producto.id === productoSeleccionado.id
            ? { ...item, cantidad: item.cantidad + cantidad }
            : item
        )
      );
    } else {
      setCarrito([
        ...carrito,
        {
          producto: productoSeleccionado,
          cantidad: cantidad,
          precio_unitario: productoSeleccionado.precio_venta,
          descuento: 0,
        },
      ]);
    }

    setProductoSeleccionado(null);
    setCantidad(1);
  };

  const eliminarDelCarrito = (index) => {
    const producto = carrito[index];
    setModalEliminar({
      open: true,
      index,
      producto: producto.producto,
    });
  };

  const confirmarEliminar = () => {
    setCarrito(carrito.filter((_, i) => i !== modalEliminar.index));
    setModalEliminar({ open: false, index: null, producto: null });
  };

  const cancelarEliminar = () => {
    setModalEliminar({ open: false, index: null, producto: null });
  };

  const actualizarCantidad = (index, nuevaCantidad) => {
    if (nuevaCantidad <= 0) return;
    setCarrito(
      carrito.map((item, i) => (i === index ? { ...item, cantidad: nuevaCantidad } : item))
    );
  };

  const calcularSubtotal = () => {
    return carrito.reduce(
      (sum, item) => sum + item.cantidad * parseFloat(item.precio_unitario || 0),
      0
    );
  };

  const calcularImpuesto = () => {
    return carrito.reduce((sum, item) => {
      const subtotalItem = item.cantidad * parseFloat(item.precio_unitario || 0);
      const impuesto = parseFloat(item.producto.impuesto || 0);
      return sum + subtotalItem * (impuesto / 100);
    }, 0);
  };

  const calcularTotal = () => {
    return calcularSubtotal() + calcularImpuesto() - descuento;
  };

  const procesarVenta = async () => {
    if (carrito.length === 0) {
      alert("El carrito está vacío");
      return;
    }

    if (!metodoPago) {
      alert("Seleccione un método de pago");
      return;
    }

    setProcesando(true);
    const total = calcularTotal();

    const ventaData = {
      empresa_id: 1, // Obtener de contexto
      sucursal_id: 1, // Obtener de contexto
      usuario_id: 1, // Obtener de contexto/auth
      cliente_id: clienteSeleccionado?.id || null,
      tipo_venta: "contado",
      descuento: descuento,
      detalles: carrito.map((item) => ({
        producto_id: item.producto.id,
        cantidad: item.cantidad,
        precio_unitario: parseFloat(item.precio_unitario),
      })),
      pagos: [
        {
          metodo_pago_id: metodoPago,
          monto: total,
          referencia: referencia,
        },
      ],
    };

    try {
      const response = await axios.post(API_ENDPOINTS.VENTAS, ventaData);
      const factura = response.data.venta?.numero_factura || "N/A";
      alert(
        `✅ ¡Venta procesada con éxito!\n\n📄 Factura: ${factura}\n💰 Total: $${total.toFixed(2)}`
      );

      // Limpiar formulario y estado
      setCarrito([]);
      setClienteSeleccionado(null);
      setDescuento(0);
      setMetodoPago("");
      setReferencia("");
    } catch (error) {
      console.error("Error al procesar venta:", error);

      const errorMessage =
        error.response?.data?.message || "Ocurrió un error al procesar la venta.";
      const errorDetail = error.response?.data?.error
        ? `\n\n🔍 Detalle Técnico: ${
            typeof error.response.data.error === "object"
              ? JSON.stringify(error.response.data.error)
              : error.response.data.error
          }`
        : `\n${error.message}`;

      alert(
        `❌ Error al procesar la venta\n\n${errorMessage}${errorDetail}\n\nPor favor, verifica la consola para más detalles o contacta a soporte.`
      );
    } finally {
      setProcesando(false);
    }
  };

  const metodoPagoSeleccionado = metodosPago.find((m) => m.id === metodoPago);

  return (
    <Grid container spacing={3}>
      {/* Panel Izquierdo - Búsqueda y Carrito */}
      <Grid item xs={12} md={8}>
        <Card sx={{ mb: 3 }}>
          <MDBox p={{ xs: 2, sm: 3 }}>
            <MDTypography variant="h6" fontWeight="bold" mb={2}>
              Buscar Producto
            </MDTypography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={8}>
                <MDBox>
                  <MDTypography variant="caption" fontWeight="medium" color="text" mb={1}>
                    Producto
                  </MDTypography>
                  <Autocomplete
                    options={productos}
                    getOptionLabel={(option) =>
                      `${option.codigo_producto} - ${option.nombre} - $${option.precio_venta}`
                    }
                    value={productoSeleccionado}
                    onChange={(event, newValue) => setProductoSeleccionado(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar por código o nombre"
                        size="small"
                      />
                    )}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={6} md={2}>
                <MDBox>
                  <MDTypography variant="caption" fontWeight="medium" color="text" mb={1}>
                    Cantidad
                  </MDTypography>
                  <MDInput
                    type="number"
                    placeholder="1"
                    value={cantidad}
                    onChange={(e) => setCantidad(parseInt(e.target.value) || 1)}
                    inputProps={{ min: 1 }}
                    size="small"
                    fullWidth
                  />
                </MDBox>
              </Grid>
              <Grid item xs={6} md={2}>
                <MDButton
                  variant="gradient"
                  color={sidenavColor}
                  fullWidth
                  onClick={agregarAlCarrito}
                  disabled={!productoSeleccionado}
                  sx={{ py: 1.2 }}
                >
                  <Icon sx={{ mr: { xs: 0, sm: 1 } }}>add_shopping_cart</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    Agregar
                  </MDBox>
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </Card>

        <Card>
          <MDBox p={{ xs: 2, sm: 3 }}>
            <MDTypography variant="h6" fontWeight="bold" mb={2}>
              Carrito de Compras
            </MDTypography>

            {carrito.length === 0 ? (
              <MDBox
                py={6}
                textAlign="center"
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "grey.50",
                  borderRadius: 2,
                }}
              >
                <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}>shopping_cart</Icon>
                <MDTypography variant="body2" color="text">
                  El carrito está vacío
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Agregue productos para comenzar
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox
                sx={{
                  maxHeight: "380px",
                  overflowY: "auto",
                  pr: 1,
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "grey.100",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "grey.400",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: "grey.500",
                    },
                  },
                }}
              >
                {carrito.map((item, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 2,
                      border: "1px solid",
                      borderColor: "grey.300",
                      boxShadow: 1,
                      transition: "all 0.3s",
                      "&:hover": {
                        boxShadow: 3,
                        borderColor: "grey.400",
                      },
                    }}
                  >
                    <MDBox p={{ xs: 1.5, sm: 2 }}>
                      <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
                        {/* Información del Producto */}
                        <Grid item xs={12} sm={5}>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            fontSize={{ xs: "0.8rem", sm: "0.875rem" }}
                            sx={{
                              display: "block",
                              mb: 0.5,
                              wordBreak: "break-word",
                            }}
                          >
                            {item.producto.nombre}
                          </MDTypography>
                          <MDBox
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              gap: { xs: 0.5, sm: 2 },
                            }}
                          >
                            <MDTypography variant="caption" color="text">
                              <strong>Código:</strong> {item.producto.codigo_producto}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              <strong>Precio:</strong> $
                              {parseFloat(item.precio_unitario).toFixed(2)}
                            </MDTypography>
                          </MDBox>
                        </Grid>

                        {/* Controles de Cantidad */}
                        <Grid item xs={6} sm={3}>
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              fontWeight="medium"
                              color="text"
                              display="block"
                              mb={0.5}
                            >
                              Cantidad
                            </MDTypography>
                            <MDBox
                              display="flex"
                              alignItems="center"
                              sx={{
                                backgroundColor: "grey.100",
                                borderRadius: 1,
                                p: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => actualizarCantidad(index, item.cantidad - 1)}
                                sx={{ p: { xs: 0.5, sm: 1 } }}
                              >
                                <Icon fontSize="small">remove</Icon>
                              </IconButton>
                              <MDTypography
                                variant="body2"
                                fontWeight="bold"
                                mx={{ xs: 1, sm: 1.5 }}
                                minWidth="30px"
                                textAlign="center"
                              >
                                {item.cantidad}
                              </MDTypography>
                              <IconButton
                                size="small"
                                onClick={() => actualizarCantidad(index, item.cantidad + 1)}
                                sx={{ p: { xs: 0.5, sm: 1 } }}
                              >
                                <Icon fontSize="small">add</Icon>
                              </IconButton>
                            </MDBox>
                          </MDBox>
                        </Grid>

                        {/* Subtotal */}
                        <Grid item xs={4} sm={3}>
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              fontWeight="medium"
                              color="text"
                              display="block"
                              mb={0.5}
                            >
                              Subtotal
                            </MDTypography>
                            <MDTypography
                              variant="h6"
                              color="success"
                              fontWeight="bold"
                              fontSize={{ xs: "0.95rem", sm: "1rem" }}
                            >
                              ${(item.cantidad * parseFloat(item.precio_unitario)).toFixed(2)}
                            </MDTypography>
                          </MDBox>
                        </Grid>

                        {/* Botón Eliminar */}
                        <Grid item xs={2} sm={1}>
                          <MDBox display="flex" justifyContent="center">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => eliminarDelCarrito(index)}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "error.light",
                                  color: "white",
                                },
                              }}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </IconButton>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                ))}
              </MDBox>
            )}
          </MDBox>
        </Card>
      </Grid>

      {/* Panel Derecho - Resumen y Pago */}
      <Grid item xs={12} md={4}>
        <Card sx={{ position: { md: "sticky" }, top: 20 }}>
          <MDBox p={2}>
            <MDTypography variant="h6" fontWeight="bold" mb={2}>
              Resumen de Venta
            </MDTypography>

            {/* Cliente */}
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                Cliente (Opcional)
              </MDTypography>
              <Autocomplete
                options={clientes}
                getOptionLabel={(option) =>
                  `${option.rif || option.numero_documento || "S/N"} - ${option.nombre}`
                }
                value={clienteSeleccionado}
                onChange={(event, newValue) => setClienteSeleccionado(newValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Seleccione un cliente" size="small" />
                )}
              />
            </MDBox>

            <Divider sx={{ my: 1.5 }} />

            {/* Cálculos */}
            <MDBox mb={1.5}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="body2" color="text">
                  Subtotal:
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  ${calcularSubtotal().toFixed(2)}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="body2" color="text">
                  Impuesto:
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  ${calcularImpuesto().toFixed(2)}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="body2" color="text" sx={{ mr: 1 }}>
                  Descuento:
                </MDTypography>
                <MDBox sx={{ minWidth: 100 }}>
                  <MDInput
                    type="number"
                    value={descuento}
                    onChange={(e) => setDescuento(parseFloat(e.target.value) || 0)}
                    size="small"
                    inputProps={{
                      min: 0,
                      step: 0.01,
                      style: { textAlign: "right", fontSize: "0.875rem" },
                    }}
                  />
                </MDBox>
              </MDBox>
            </MDBox>

            <Divider sx={{ my: 1.5 }} />

            {/* Total */}
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              p={1.5}
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "grey.100",
                borderRadius: 1.5,
              }}
            >
              <MDTypography variant="body1" fontWeight="bold">
                Total:
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold" color={sidenavColor}>
                ${calcularTotal().toFixed(2)}
              </MDTypography>
            </MDBox>

            {/* Método de Pago */}
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                Método de Pago
              </MDTypography>
              <FormControl fullWidth size="small">
                <Select
                  value={metodoPago}
                  onChange={(e) => setMetodoPago(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: "0.875rem" }}
                >
                  <MenuItem value="" disabled>
                    Seleccione un método
                  </MenuItem>
                  {metodosPago.map((metodo) => (
                    <MenuItem key={metodo.id} value={metodo.id}>
                      {metodo.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            {/* Referencia */}
            {metodoPagoSeleccionado?.requiere_referencia && (
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                  Referencia
                </MDTypography>
                <MDInput
                  type="text"
                  placeholder="Ingrese la referencia"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  fullWidth
                  size="small"
                />
              </MDBox>
            )}

            {/* Botones de Acción */}
            <MDBox>
              <MDButton
                variant="gradient"
                color={sidenavColor}
                fullWidth
                onClick={procesarVenta}
                disabled={carrito.length === 0 || procesando}
                sx={{ mb: 1, py: 1.2 }}
              >
                <Icon sx={{ mr: 1, fontSize: "1.1rem" }}>{procesando ? "sync" : "payment"}</Icon>
                <MDTypography variant="button" color="white" fontWeight="medium">
                  {procesando ? "Procesando..." : "Procesar Venta"}
                </MDTypography>
              </MDButton>

              <MDButton
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => setCarrito([])}
                disabled={carrito.length === 0}
                sx={{ py: 1.2 }}
              >
                <Icon sx={{ mr: 1, fontSize: "1.1rem" }}>clear</Icon>
                <MDTypography variant="button" fontWeight="medium">
                  Limpiar Carrito
                </MDTypography>
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>

      {/* Modal de confirmación para eliminar */}
      <Dialog
        open={modalEliminar.open}
        onClose={cancelarEliminar}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon fontSize="large" color="error">
              warning
            </Icon>
            <MDTypography variant="h5" fontWeight="medium">
              Confirmar Eliminación
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
            ¿Está seguro que desea eliminar el siguiente producto del carrito?
          </MDTypography>

          <Card
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              border: "1px solid rgba(244, 67, 54, 0.3)",
            }}
          >
            <MDTypography variant="h6" fontWeight="bold" color="dark">
              {modalEliminar.producto?.nombre || ""}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {modalEliminar.producto?.descripcion || ""}
            </MDTypography>
          </Card>

          <MDTypography variant="caption" color="text" sx={{ mt: 2, display: "block" }}>
            Esta acción no se puede deshacer.
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="dark" onClick={cancelarEliminar}>
            <Icon>close</Icon>&nbsp;Cancelar
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={confirmarEliminar}>
            <Icon>delete</Icon>&nbsp;Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
